import { FormControl } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FoodsSelectServerPagination } from "@foodpilot/foods";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useProductLineSelector, SelectorProductLine } from "src/api/client-api/productLines/useProductLineSelector";

interface FormProductLineSelectorProps {
    value: string;
    onChange: (value: string) => void;
    excludeId?: number;
}

export const FormProductLineSelector = ({ value, onChange, excludeId }: FormProductLineSelectorProps) => {
    const { t } = useTranslation();
    const { search, delayedSearch, setSearch } = useDelayedSearch();

    const { productLines, isLoading } = useProductLineSelector({
        searchText: delayedSearch,
        excludeId: excludeId,
    });

    const selectedProductLine = productLines.find((p) => p["@id"] === value);

    const isSearchFound = (searchValue: string) => {
        if (search === "") return true;
        return searchValue.toLowerCase().includes(search.toLowerCase());
    };

    return (
        <FormControl fullWidth>
            <FoodsSelectServerPagination
                selectedOption={selectedProductLine || null}
                options={isLoading ? [] : productLines}
                onChange={(productLine) => onChange(productLine ? productLine["@id"] : "")}
                getId={(productLine: SelectorProductLine) => productLine["@id"]}
                getName={(productLine: SelectorProductLine) => {
                    let name = productLine.name;
                    if (productLine.parent) {
                        name = `${productLine.parent.name} > ${name}`;
                    }
                    return name;
                }}
                textOptions={{
                    placeholder: t("productLine.select.placeholder", "Select a parent product line"),
                    searchPlaceholder: t("productLine.select.searchPlaceholder", "Search product lines..."),
                    emptyOptionsText: t("productLine.select.noOptions", "No product lines available"),
                    defaultValueText: t("productLine.select.defaultValue", "Default value"),
                    noResult:
                        isLoading ?
                            t("productLine.select.loading", "Loading...")
                        :   t("productLine.select.noResultFound", "No product lines found"),
                }}
                search={search}
                setSearch={setSearch}
                isSearchFound={isSearchFound}
            />
        </FormControl>
    );
};
